<template>
    <vx-expandable-card>
        <template v-slot:header> Billing Information </template>
        <template v-slot:content>
            <div class="flex flex-col gap-4 mt-3">
                <div>
                    <vx-label size="md" class="w-full">
                        <div class="flex items-center justify-between flex-grow">
                            <span>Company Legal Name</span>
                            <vx-badge color="slate" size="xs">
                                <span class="pb-[1.5px] text-xs leading-snug">Optional</span>
                                <font-awesome-icon widthAuto :icon="faPencilSlash" size="xs" />
                            </vx-badge>
                        </div>
                    </vx-label>

                    <vx-input
                        data-test="company-legal-name-input"
                        v-model="billingInfoForm.company_legal_name"
                        size="lg"
                        :error="displayFirstError(validate.company_legal_name.$errors)"
                        :disabled="!props.isEditable"
                    ></vx-input>
                </div>

                <div>
                    <vx-label size="md">Address</vx-label>

                    <vx-input
                        data-test="address-1-input"
                        v-model="billingInfoForm.address"
                        size="lg"
                        :error="displayFirstError(validate.address.$errors)"
                        :disabled="!props.isEditable"
                    ></vx-input>
                </div>

                <div>
                    <vx-label size="md" class="w-full">
                        <div class="flex items-center justify-between flex-grow">
                            <span>Address Line 2</span>
                            <vx-badge color="slate" size="xs">
                                <span class="pb-[1.5px] text-xs leading-snug">Optional</span>
                                <font-awesome-icon widthAuto :icon="faPencilSlash" size="xs" />
                            </vx-badge>
                        </div>
                    </vx-label>

                    <vx-input
                        data-test="address-2-input"
                        v-model="billingInfoForm.address_line_2"
                        size="lg"
                        :error="displayFirstError(validate.address_line_2.$errors)"
                        :disabled="!props.isEditable"
                    ></vx-input>
                </div>

                <div>
                    <vx-label size="md">Country</vx-label>
                    <vx-selectable
                        data-test="country-input"
                        v-model="billingInfoForm.country"
                        :options="countries"
                        :reduce="(option) => option.value"
                        size="lg"
                        :error="displayFirstError(validate.country.$errors)"
                        :clearable="false"
                        :disabled="!props.isEditable"
                    ></vx-selectable>
                </div>

                <div>
                    <vx-label size="md">City</vx-label>

                    <vx-input
                        data-test="city-input"
                        v-model="billingInfoForm.city"
                        size="lg"
                        :error="displayFirstError(validate.city.$errors)"
                        :disabled="!props.isEditable"
                    ></vx-input>
                </div>

                <div>
                    <vx-label size="md">State</vx-label>

                    <vx-selectable
                        v-if="showStateDropdown"
                        data-test="state-select"
                        v-model="billingInfoForm.state"
                        :options="states[billingInfoForm.country || 'US']"
                        :reduce="(option) => option.value"
                        size="lg"
                        :error="displayFirstError(validate.state.$errors)"
                        :clearable="false"
                        :disabled="!props.isEditable"
                        @update:modelValue="onStatePicked"
                    ></vx-selectable>
                    <vx-input
                        v-else
                        data-test="state-input"
                        v-model="billingInfoForm.state"
                        size="lg"
                        :error="displayFirstError(validate.state.$errors)"
                        :disabled="!props.isEditable"
                    ></vx-input>
                </div>

                <div>
                    <vx-label size="md">Zip</vx-label>

                    <vx-input
                        data-test="zip-input"
                        v-model="billingInfoForm.zip"
                        size="lg"
                        :error="displayFirstError(validate.zip.$errors)"
                        :disabled="!props.isEditable"
                    ></vx-input>
                </div>

                <div>
                    <vx-label size="md" class="w-full">
                        <div class="flex items-center justify-between flex-grow">
                            <span>Purchase Order Number</span>
                            <vx-badge color="slate" size="xs">
                                <span class="pb-[1.5px] text-xs leading-snug">Optional</span>
                                <font-awesome-icon widthAuto :icon="faPencilSlash" size="xs" />
                            </vx-badge>
                        </div>
                    </vx-label>

                    <vx-input
                        data-test="po-number-input"
                        v-model="billingInfoForm.po_number"
                        size="lg"
                        :error="displayFirstError(validate.po_number.$errors)"
                        :disabled="!props.isEditable"
                    ></vx-input>
                </div>

                <div v-if="props.isEditable" class="flex gap-2">
                    <vx-button
                        type="button"
                        size="lg"
                        @click="saveBillingInformation"
                        :loading="saving"
                        :disabled="!props.isEditable"
                        class="flex-1"
                        data-test="update-billing-info-button"
                    >
                        Update
                    </vx-button>
                    <vx-button
                        v-if="props.allowUnset"
                        type="button"
                        size="lg"
                        color="danger"
                        :loading="props.unsetting"
                        class="flex-1"
                        data-test="unset-billing-information"
                        @click="$emit('unset')"
                    >
                        Unset
                    </vx-button>
                </div>
            </div>
        </template>
    </vx-expandable-card>
</template>

<script setup>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faPencilSlash } from '@fortawesome/pro-solid-svg-icons';
import { VxButton, VxExpandableCard, VxInput, VxLabel, VxSelectable, VxBadge } from '@voxie/frontend-components';
import useVuelidate from '@vuelidate/core';
import { alphaNum, helpers, maxLength, minLength, numeric, required } from '@vuelidate/validators';
import { ref, computed } from 'vue';
import { orgUnitBillingClient } from '~/services';
import { useToasted } from '~/composables/useToasted';
import { catchRequestError } from '~/utils/request';
import { displayFirstError } from '~/utils/validation';
import { countries, states } from '~/constants/countries';
import { useAddressStateSelector } from '~/composables/useAddressStateSelector';

const props = defineProps({
    teamId: {
        type: Number,
        required: true,
    },
    orgUnitId: {
        type: Number,
        required: true,
    },
    billingInformation: {
        type: Object,
        required: true,
    },
    isEditable: {
        type: Boolean,
        default: false,
    },
    allowUnset: {
        type: Boolean,
        default: false,
    },
    unsetting: {
        type: Boolean,
        default: false,
    },
});

defineEmits(['unset']);

const toasted = useToasted();
const saving = ref(false);

const billingInfoForm = ref({
    company_legal_name: props.billingInformation.billing_company_legal_name || null,
    address: props.billingInformation.billing_address || '',
    address_line_2: props.billingInformation.billing_address_line_2 || null,
    city: props.billingInformation.billing_city || '',
    state: props.billingInformation.billing_state || '',
    zip: props.billingInformation.billing_zip || '',
    country: props.billingInformation.billing_country || '',
    po_number: props.billingInformation.billing_po_number || null,
});

const { showStateDropdown, onStatePicked } = useAddressStateSelector(billingInfoForm, { emptyValue: '' });

const rules = computed(() => {
    const defaultRules = {
        company_legal_name: { maxLength: maxLength(100) },
        address: { required: required, maxLength: maxLength(100) },
        address_line_2: { maxLength: maxLength(100) },
        city: { required: required, maxLength: maxLength(50) },
        state: { required: required, maxLength: maxLength(50) },
        country: { required: required, maxLength: maxLength(2) },
        zip: {
            required: required,
            alphaNum: helpers.withMessage('The ZIP code must be alpha-numeric', alphaNum),
            maxLength: maxLength(25),
        },
        po_number: {
            maxLength: maxLength(40),
            alphaNumDashUnderscore: helpers.withMessage(
                'The PO number must only contain letters, numbers, hyphens, underscores, and spaces',
                helpers.regex(/^[a-zA-Z0-9\-_\s]*$/)
            ),
        },
    };

    if (showStateDropdown.value) {
        defaultRules.state.maxLength = maxLength(2);
    }

    if (['US', 'CA'].includes(billingInfoForm.value.country)) {
        const zipCodeLength = billingInfoForm.value.country === 'CA' ? 6 : 5;
        defaultRules.zip = {
            ...defaultRules.zip,
            minLength: minLength(zipCodeLength),
            maxLength: maxLength(zipCodeLength),
        };

        if (billingInfoForm.value.country === 'US') {
            delete defaultRules.zip.alphaNum;
            defaultRules.zip.numeric = helpers.withMessage('The ZIP code must only contain numbers', numeric);
        }
    }

    return defaultRules;
});

const validate = useVuelidate(rules, billingInfoForm);

const saveBillingInformation = async () => {
    if (saving.value) {
        return;
    }

    saving.value = true;

    validate.value.$touch();

    if (validate.value.$invalid) {
        saving.value = false;
        return;
    }

    try {
        await orgUnitBillingClient.updateInformation(props.teamId, props.orgUnitId, billingInfoForm.value);
        toasted.success('Billing information has been successfully updated');
    } catch (e) {
        toasted.error(catchRequestError(e));
    } finally {
        saving.value = false;
    }
};
</script>
